import './polyfills' // Must be first — sets up Buffer global before AWS SDK modules load

// Install console capture before anything else logs, so support tickets can
// attach the full session log. Silent mode mirrors the previous prod silencer:
// log/info/debug stop reaching the real console, but warn/error still do, and
// everything is still captured into the ring buffer.
import ConsoleLogCapture from './services/ConsoleLogCapture'
ConsoleLogCapture.install({
  silent: process.env.VUE_APP_AWS_ENV === 'prod' && (typeof localStorage === 'undefined' || localStorage.getItem('debug') !== 'true')
})

//these imports replace the stylesheet ref's in index.html
// <link rel="stylesheet" href="https://fonts.googleapis.com/css?family=Roboto:100,300,400,500,700,900">
//   <link rel="stylesheet" href="https://fonts.googleapis.com/css?family=Material+Icons">
import './static/css/google.materialicons.css'
import './static/css/google.roboto.css'

import { createApp } from 'vue'

import store from './store'
import vuetify from './plugins/vuetify';

import VueSweetalert2 from 'vue-sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

// eslint-disable-next-line no-console
console.log(`version=${process.env.VUE_APP_VERSION}`)

import App from './App.vue'

import Auth from "@aws-amplify/auth";
import trupledevawsconfig from "./amplify/truple/dev/aws-exports";
import trupleprodawsconfig from "./amplify/truple/prod/aws-exports";
import badevawsconfig from "./amplify/bestaccountability/dev/aws-exports";
import baprodawsconfig from "./amplify/bestaccountability/prod/aws-exports";

let cfg
if(process.env.VUE_APP_PRODUCT === 'bestaccountability') {
  cfg = process.env.VUE_APP_AWS_ENV === 'prod' ? baprodawsconfig : badevawsconfig
} else {
  cfg = process.env.VUE_APP_AWS_ENV === 'prod' ? trupleprodawsconfig : trupledevawsconfig
}

if(process.env.VUE_APP_ENV_AMPLIFY_CLIENT_ID_OVERRIDE) {
  // eslint-disable-next-line no-console
  console.log('process.env.VUE_APP_ENV_AMPLIFY_CLIENT_ID_OVERRIDE')
}

if(process.env.VUE_APP_LOCAL === "true") {
  // eslint-disable-next-line no-console
  console.log('process.env.VUE_APP_LOCAL ' + process.env.VUE_APP_LOCAL)
  cfg.Auth.oauth.redirectSignIn = 'http://localhost:8080/'
  cfg.Auth.oauth.redirectSignOut = 'http://localhost:8080/'
}

// Prod console silencing is handled by ConsoleLogCapture's silent mode (installed at the top).

Auth.configure(cfg)

// Patch Amplify's Credentials._setCredentialsFromSession to:
// 1. Cache the Cognito Identity ID in localStorage (keyed by sub) so subsequent page loads
//    skip the GetId call entirely — going straight to GetCredentialsForIdentity.
// 2. Deduplicate concurrent calls — Amplify's Hub listener and our explicit Auth.currentCredentials()
//    both call _setCredentialsFromSession independently (no shared dedup guard), causing
//    TooManyRequestsException on GetId. This ensures only one call runs at a time.
import { CredentialsClass } from '@aws-amplify/core'
import { CognitoIdentityClient, GetIdCommand } from '@aws-sdk/client-cognito-identity'
import { fromCognitoIdentity } from '@aws-sdk/credential-provider-cognito-identity'

function _cachedSetCredentialsFromSession(session) {
  var self = this
  var idToken = session.getIdToken().getJwtToken()
  var { region, userPoolId, identityPoolId } = this._config

  if (!identityPoolId) return Promise.reject('No Cognito Federated Identity pool provided')
  if (!region) return Promise.reject('region is not configured for getting the credentials')

  var loginKey = 'cognito-idp.' + region + '.amazonaws.com/' + userPoolId
  var logins = {}
  logins[loginKey] = idToken

  // Decode JWT to get sub for cache key
  var sub = ''
  try {
    sub = JSON.parse(atob(idToken.split('.')[1])).sub || ''
  } catch (e) { /* ignore */ }

  var cacheKey = 'CognitoIdentityId-' + identityPoolId + '-' + sub
  var cachedIdentityId = localStorage.getItem(cacheKey)
  var cognitoClient = new CognitoIdentityClient({ region: region })

  var credentialsProvider = async function() {
    if (cachedIdentityId) {
      try {
        console.log('Using cached Cognito identity ID')
        var creds = await fromCognitoIdentity({
          client: cognitoClient,
          logins: logins,
          identityId: cachedIdentityId
        })()
        self._identityId = cachedIdentityId
        return creds
      } catch (e) {
        console.log('Cached identity ID failed, falling back to GetId', e)
        localStorage.removeItem(cacheKey)
      }
    }

    // No cache or cache was invalid — call GetId
    console.log('Calling GetId for Cognito identity')
    var result = await cognitoClient.send(new GetIdCommand({
      IdentityPoolId: identityPoolId,
      Logins: logins
    }))
    var identityId = result.IdentityId
    self._identityId = identityId

    try {
      localStorage.setItem(cacheKey, identityId)
    } catch (e) { /* storage full */ }

    return fromCognitoIdentity({
      client: cognitoClient,
      logins: logins,
      identityId: identityId
    })()
  }

  var credentials = credentialsProvider()
  return this._loadCredentials(credentials, 'userPool', true, null)
}

let _inflightSessionCredentials = null
CredentialsClass.prototype._setCredentialsFromSession = function(session) {
  if (_inflightSessionCredentials) {
    return _inflightSessionCredentials
  }
  _inflightSessionCredentials = _cachedSetCredentialsFromSession.call(this, session).finally(() => {
    _inflightSessionCredentials = null
  })
  return _inflightSessionCredentials
}

// Patch Amplify's Credentials._setCredentialsFromFederation to cache the Cognito Identity ID
// in localStorage (same approach as the session patch above). This path is used by the Android
// app's appLogin flow via Auth.federatedSignIn, which otherwise calls fromCognitoIdentityPool
// (triggering a GetId call that gets rate-limited).
function _cachedSetCredentialsFromFederation(params) {
  var self = this
  var provider = params.provider, token = params.token, identity_id = params.identity_id

  var domains = {
    google: 'accounts.google.com',
    facebook: 'graph.facebook.com',
    amazon: 'www.amazon.com',
    developer: 'cognito-identity.amazonaws.com',
  }
  var domain = domains[provider] || provider
  if (!domain) {
    return Promise.reject('You must specify a federated provider')
  }

  var logins = {}
  logins[domain] = token

  var identityPoolId = this._config.identityPoolId
  var region = this._config.region
  if (!identityPoolId) return Promise.reject('No Cognito Federated Identity pool provided')
  if (!region) return Promise.reject('region is not configured for getting the credentials')

  var cognitoClient = new CognitoIdentityClient({ region: region })

  // If identity_id already provided, use it directly (no GetId needed)
  if (identity_id) {
    var credentials = fromCognitoIdentity({
      identityId: identity_id,
      logins: logins,
      client: cognitoClient,
    })()
    return this._loadCredentials(credentials, 'federated', true, params)
  }

  // Try to decode sub from token for cache key
  var sub = ''
  try {
    sub = JSON.parse(atob(token.split('.')[1])).sub || ''
  } catch (e) { /* not a JWT or decode failed */ }

  // Include domain in cache key so different auth methods (email/password vs Google) don't collide
  var cacheKey = 'CognitoIdentityId-' + identityPoolId + '-' + domain + '-' + sub
  var cachedIdentityId = sub ? localStorage.getItem(cacheKey) : null

  var credentialsProvider = async function() {
    if (cachedIdentityId) {
      try {
        console.log('Federation: Using cached Cognito identity ID')
        var creds = await fromCognitoIdentity({
          client: cognitoClient,
          logins: logins,
          identityId: cachedIdentityId
        })()
        self._identityId = cachedIdentityId
        return creds
      } catch (e) {
        console.log('Federation: Cached identity ID failed, falling back to GetId', e)
        localStorage.removeItem(cacheKey)
      }
    }

    // No cache or cache was invalid — call GetId
    console.log('Federation: Calling GetId for Cognito identity')
    var result = await cognitoClient.send(new GetIdCommand({
      IdentityPoolId: identityPoolId,
      Logins: logins
    }))
    var identityId = result.IdentityId
    self._identityId = identityId

    if (sub) {
      try {
        localStorage.setItem(cacheKey, identityId)
      } catch (e) { /* storage full */ }
    }

    return fromCognitoIdentity({
      client: cognitoClient,
      logins: logins,
      identityId: identityId
    })()
  }

  var credentials = credentialsProvider()
  return this._loadCredentials(credentials, 'federated', true, params)
}

let _inflightFederationCredentials = null
CredentialsClass.prototype._setCredentialsFromFederation = function(params) {
  if (_inflightFederationCredentials) {
    return _inflightFederationCredentials
  }
  _inflightFederationCredentials = _cachedSetCredentialsFromFederation.call(this, params).finally(() => {
    _inflightFederationCredentials = null
  })
  return _inflightFederationCredentials
}

import VueChartkick from 'vue-chartkick'
import 'chartkick/chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { Chart } from 'chart.js'
Chart.register(ChartDataLabels)
Chart.defaults.plugins.datalabels = Chart.defaults.plugins.datalabels || {}
Chart.defaults.plugins.datalabels.anchor = 'end'
Chart.defaults.plugins.datalabels.clamp = true
Chart.defaults.plugins.datalabels.align = 'end'

import router from './lib/router'

import auth from './lib/auth'
import api from './lib/api'

// Load Segment analytics (was previously in index.html)
if (window.analytics && process.env.VUE_APP_SEGMENT_KEY) {
  window.analytics.load(process.env.VUE_APP_SEGMENT_KEY)
}

import EncryptionGenBadge from './components/e2ee/EncryptionGenBadge.vue'

const app = createApp(App)

// Used on every encryption surface to tell the two generations apart.
app.component('EncryptionGenBadge', EncryptionGenBadge)

app.use(store)
app.use(router)
app.use(vuetify)
app.use(VueSweetalert2, {
  confirmButtonColor: process.env.VUE_APP_COLOR_PRIMARY,
  cancelButtonColor: process.env.VUE_APP_COLOR_SECONDARY,
})
app.use(VueChartkick)
app.config.globalProperties.auth = auth
app.config.globalProperties.api = api

app.config.devtools = process.env.VUE_APP_AWS_ENV === 'dev'

app.mount('#app')
